// Video operations API
import { getAPI, token, mangomoloUploadEndpoint } from "@/api/params.js";
import { MEDIA_UPDATE_INTERVAL, MEDIA_CONVERSION_STATUS } from "@/constants.js";

// Key that uses to stop videos updatings
let videosIntervalKey = null;

// Function, that handles new videos list
let videosUpdateCallback = null;
// Function, that executes when error occured
let videosErrorCallback = null;

/**
 * Get server-format clip data and convert to client-format
 * @param {Object} itemData server-format clip data
 * @returns client-format clip data
 */
function parseVideo(itemData) {
  return {
    id: itemData.id,
    title: itemData.tags?.title ?? "Add title",
    description: itemData.tags?.description ?? null,
    stream: itemData.stream ?? null,
    status: itemData.status ?? MEDIA_CONVERSION_STATUS.READY,
    progress: +itemData.conversionPercentage || 0,
    isRemovable: itemData.canBeRemoved ?? true,
    // isUploadedToMangomolo: itemData.isUploadedToMangomolo ?? false,
    // isUploadingToMangomolo: itemData.isStartedUploadingToMM ?? false,
    multistepId: itemData.multistepId,
    tags: itemData.tags ?? {},

    startTime: +itemData.startTime || 0,
    endTime: +itemData.endTime || 0,
    createdAt: +itemData.createdAt || 0,
    storageUrl: itemData.storageUrl,
    isMultistepSource: itemData.isMultistepSource ?? true,

    remoteInfo: itemData.remoteInfo ?? {},
    downloadLink: itemData.downloadLink ? itemData.storageUrl +'/'+ itemData.downloadLink : itemData.remoteInfo?.downloadUrl,
    playLink: itemData.playLink ? itemData.storageUrl +'/'+  itemData.playLink : itemData.remoteInfo?.playUrl,
    thumbnailLink: itemData.thumbnailLink ? itemData.storageUrl +'/'+  itemData.thumbnailLink : itemData.remoteInfo?.thumbnailUrl,

    // downloadLink: itemData.downloadLink ?? null,
    // playLink: itemData.playLink ?? null,
    // thumbnailLink: itemData.thumbnailLink ?? null,
    previewLink: itemData.previewLink ?? null,
    isUploadedByUser: itemData.isUploadedByUser ?? false,

    metadata: itemData.metadata ?? {},
    sharings: itemData.sharings,
  };
}

/**
 * Request videos list from server
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
async function getVideos(app, stream) {
  let withoutStream = false
  if(stream === null){
    withoutStream = true
    stream = ''
  }
  const videosListRequest = `${getAPI()}/video? ${stream ? `&stream=${stream}` : ""}&token=${token}${withoutStream ? '&only_without_stream=true' : ''}`;
  const videosResponse = await fetch(videosListRequest, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: token
  }
  });
  if (!videosResponse.ok) {
    return {
      success: false,
      error: `${videosResponse.status}: ${videosResponse.statusText}`
    };
  }
  const videosObj = await videosResponse.json();
  return {
    success: true,
    items: videosObj.items
  };
}

/**
 * Get videos and execute callback function or error handler (if getting has failed)
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
async function updateVideos(app, stream) {
  const videos = await getVideos(app, stream);
  if (videos.success) {
    videosUpdateCallback.forEach(e => {
      e(videos.items.map(parseVideo));
    });
  } else {
    videosErrorCallback.forEach(e => {
      e(videos.error);
    });
  }
}

let lastApp = "undefined";
let lastStream = "undefined";

/**
 * Execute callback function when videos list updated
 * @param {String} app Application name
 * @param {String} stream Stream name
 * @param {Function} callback Callback function with one argument (videos list)
 * @param {Function} error Error function with one argument (error message)
 */

export const subscribeToVideoUpdates = (app, stream, callback, error) => {
  if (videosIntervalKey != null) {
    unsubscribeFromVideoUpdates();
  }
  if (stream == "No Stream") {
    stream = "";
  }

  if (lastApp != app || lastStream != stream) {
    videosUpdateCallback = [];
    videosErrorCallback = [];
  }
  lastApp = app;
  lastStream = stream;

  // videosUpdateCallback
  videosUpdateCallback.push(callback);
  videosErrorCallback.push(error);
  // videosErrorCallback = error;

  updateVideos(app, stream);
  videosIntervalKey = setInterval(
    () => updateVideos(app, stream),
    MEDIA_UPDATE_INTERVAL
  );
};

/**
 * Cancel callback binding to videos updating
 */
export const unsubscribeFromVideoUpdates = () => {
  clearInterval(videosIntervalKey);
  videosIntervalKey = null;
};

/**
 * Clip video from stream
 * @param {String} app Application name
 * @param {String} stream Stream name
 * @param {Number} start Start time timstamp
 * @param {Number} end End time timestamp
 * @param {String} title Generating video's name
 */
export const cropVideo = async (
  app,
  stream,
  start,
  end,
  title,
  isMultistepSource
) => {
  const cropURL = `${getAPI()}/video/generate?token=${token}`;
  const cropData = {
    app: app,
    stream: stream,
    startTime: start,
    endTime: end,
    tags: {
      title: title
    },
    isMultistepSource: isMultistepSource,
  };
  const resp = await fetch(cropURL, {
    method: "POST",
    body: JSON.stringify(cropData),
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: token
    }
  });

  const { success, error, data } = await resp.json();

  if (!success) {
    throw new Error(error);
  }

  updateVideos(app, stream);

  return data.id;
};

export const getVideoById = async id => {
  const cropURL = `${getAPI()}/video/${id}?token=${token}`;

  const resp = await fetch(cropURL, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    }
  });

  const data = await resp.json();

  return data;
};

/**
 * Delete video by it's ID
 * @param {Number} id Video ID
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
export const removeVideo = async (id, app, stream) => {
  const deleteURL = `${getAPI()}/video/${id}?token=${token}`;
  const resp = await fetch(deleteURL, { method: "DELETE" });

  if (!resp.ok) {
    const { error } = await resp.json();
    console.error(error);
    throw Error(error);
  }

  updateVideos(app, stream);
};

/**
 * Delete all videos for specified channel
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
export const removeAllVideos = async (app, stream) => {
  if(stream === 'No Stream'){
    stream = ''
  }
  let withoutStream = false
  if(stream === null){
    withoutStream = true
    stream = ''
  }
  const deleteURL = `${getAPI()}/video?app=${app}&stream=${stream}&token=${token}${withoutStream ? '&only_without_stream=true' : ''}`;
  const resp = await fetch(deleteURL, { method: "DELETE" });

  if (!resp.ok) {
    const { error } = await resp.json();
    console.error(error);
    throw Error(error);
  }
  stream = null
  updateVideos(app, stream);
};

/**
 * Set title of video
 * @param {Number} id Video ID
 * @param {String} title New title
 * @param {String} app Application name
 * @param {String} stream Stream name
 */
export const setVideoTitle = async (id, title, app, stream) => {
  const setURL = `${getAPI()}/video/${id}?token=${token}`;
  const body = {
    title: title
  };
  const resp = await fetch(setURL, {
    body: JSON.stringify(body),
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  });

  if (!resp.ok) {
    const { error } = await resp.json();
    throw Error(error);
  }

  updateVideos(app, stream);
};

export const setVideoDescription = async (id, description, app, stream) => {
  const setURL = `${getAPI()}/video/${id}?token=${token}`;
  const body = {
    description: description
  };
  const resp = await fetch(setURL, {
    body: JSON.stringify(body),
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  });

  if (!resp.ok) {
    const { error } = await resp.json();
    throw Error(error);
  }
};

/**
 * Recreate video thumbnail, getting it from random time
 * @param {Number} videoId Video ID
 */
export const generateThumbnail = async videoId => {
  const generateURL = `${getAPI()}/video/${videoId}/generatethumbnail?token=${token}`;
  const resp = await fetch(generateURL, { method: "POST" });
  const { success, data, error } = await resp.json();

  if (!success) {
    throw Error(error);
  }

  return data;
};

/**
 * Send request to Mangomolo to upload video from link
 * @param {String} link video link (HTTP)
 * @param {String} channelId Mangomolo channel ID
 * @param {String} title video title
 */

export const uploadToMangomolo = async (
  videoId,
  mangomoloLink,
  title,
  title_arabic,
  description,
  stream,
  categoryId,
  mmSourceVideoId,
  multistepId,
  target_channel_id,
  publish
) => {
  const linkURL = `${getAPI()}/video/${videoId}/share/mangomolo?token=${token}${
    multistepId ? "&multistepId=" + multistepId : ""
  }`;
  const resp = await fetch(linkURL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      videoId,
      title,
      title_arabic,
      description,
      stream,
      multistepId,
      categoryId,
      mmSourceVideoId,
      mangomoloLink,
      target_channel_id,
      publish
    })
  });
  let response = await resp.json();
  if (!resp.ok) {
    throw new Error(`${resp.status}: ${resp.statusText}`);
  }
  return response
};

// export const uploadToMangomolo = async (link, channelId, title) => {
//   const body = new FormData();

//   body.append("token", token);
//   body.append("url", link);
//   body.append("channel_id", channelId);
//   body.append("title", title);

//   const resp = await fetch(mangomoloUploadEndpoint, {
//     method: "POST",
//     body
//   })

//   if (!resp.ok) {
//     throw new Error(`${resp.status}: ${resp.statusText}`);
//   }
// }
